class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    this.hideCaffynUpsellStep();
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      initCaffynCartUpsells(this);
      const bottleUpsellDismissed = isCaffynBottleUpsellDismissed();
      const bottleUpsell = this.querySelector('[data-caffyn-cart-upsell]');
      if (bottleUpsellDismissed && bottleUpsell) bottleUpsell.hidden = true;

      if (!bottleUpsellDismissed && this.dataset.caffynShowUpsellStep === 'true' && bottleUpsell) {
        this.showCaffynUpsellStep();
      } else {
        this.hideCaffynUpsellStep();
      }
      delete this.dataset.caffynShowUpsellStep;
      this.open();
    });
  }

  showCaffynUpsellStep() {
    this.classList.add('caffyn-upsell-step-active');
    const heading = this.querySelector('.drawer__heading');
    if (heading) heading.textContent = 'Complete your mix';
  }

  hideCaffynUpsellStep() {
    this.classList.remove('caffyn-upsell-step-active');
    const heading = this.querySelector('.drawer__heading');
    if (heading) heading.textContent = 'Your cart';
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

function formatCaffynMoney(cents, moneyFormat) {
  const value = (Number(cents || 0) / 100).toFixed(2);
  const rounded = String(Math.round(Number(cents || 0) / 100));
  const noTrailingZeros = value.replace(/\.00$/, '');
  const format = moneyFormat || '${{amount}}';

  return format
    .replace(/\{\{\s*amount\s*\}\}/, noTrailingZeros)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, rounded)
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, noTrailingZeros.replace('.', ','))
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, rounded.replace('.', ','));
}

function isCaffynBottleUpsellDismissed() {
  try {
    return sessionStorage.getItem('caffynBottleUpsellDismissed') === 'true';
  } catch (error) {
    return false;
  }
}

function dismissCaffynBottleUpsell() {
  try {
    sessionStorage.setItem('caffynBottleUpsellDismissed', 'true');
  } catch (error) {
    // The visible dismissal should still work if browser storage is unavailable.
  }
}

function goToCaffynCheckout() {
  window.location.href = '/checkout';
}

function refreshCaffynCartDrawer(cart) {
  return fetch(`${routes.cart_url}?section_id=cart-drawer&_=${Date.now()}`, {
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  })
    .then((response) => response.text())
    .then((responseText) => {
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const sourceInner = html.querySelector('.drawer__inner');
      const targetInner = cart.querySelector('.drawer__inner');
      if (!sourceInner || !targetInner) throw new Error('Unable to refresh cart drawer.');

      targetInner.innerHTML = sourceInner.innerHTML;
      initCaffynCartUpsells(cart);
      cart.hideCaffynUpsellStep();
      cart.open();
    });
}

function initCaffynCartUpsells(scope = document) {
  scope.querySelectorAll('[data-caffyn-cart-upsell]').forEach((root) => {
    if (isCaffynBottleUpsellDismissed()) {
      root.hidden = true;
      return;
    }

    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const colorButtons = Array.from(root.querySelectorAll('[data-caffyn-bottle-color-value]'));
    const sizeButtons = Array.from(root.querySelectorAll('[data-caffyn-bottle-size-value]'));
    const colorLabel = root.querySelector('[data-caffyn-bottle-color-label]');
    const priceEl = root.querySelector('[data-caffyn-bottle-price]');
    const imageEl = root.querySelector('.caffyn-cart-upsell__image');
    const button = root.querySelector('[data-caffyn-bottle-add]');
    const skipButton = root.querySelector('[data-caffyn-bottle-skip]');
    const message = root.querySelector('[data-caffyn-bottle-message]');
    const variantDataEl = root.querySelector('[data-caffyn-bottle-variants]');
    const moneyFormat = root.dataset.moneyFormat;
    let variants = [];

    try {
      variants = variantDataEl ? JSON.parse(variantDataEl.textContent || '[]') : [];
    } catch (error) {
      variants = [];
    }

    const selectedValue = (buttons, attribute) => {
      const selected = buttons.find((item) => item.classList.contains('is-selected'));
      return selected ? selected.getAttribute(attribute) : '';
    };

    const selectedVariant = () => {
      const color = selectedValue(colorButtons, 'data-caffyn-bottle-color-value');
      const size = selectedValue(sizeButtons, 'data-caffyn-bottle-size-value');

      return (
        variants.find((variant) => (!color || variant.color === color) && (!size || variant.size === size)) ||
        variants.find((variant) => variant.available) ||
        variants[0]
      );
    };

    const syncVariant = () => {
      const variant = selectedVariant();
      if (!variant) return;

      if (priceEl) priceEl.textContent = formatCaffynMoney(variant.price, moneyFormat);
      if (imageEl && variant.image) imageEl.src = variant.image;
      if (colorLabel) colorLabel.textContent = variant.color || '';
      if (button) {
        button.dataset.variantId = variant.id;
        button.disabled = !variant.available;
        button.textContent = variant.available ? `Add bottle - ${formatCaffynMoney(variant.price, moneyFormat)}` : 'Sold out';
      }
      if (message) message.textContent = '';
    };

    const addBottle = () => {
      const variant = selectedVariant();
      const cart = document.querySelector('cart-drawer');
      if (root.dataset.addingBottle === 'true') return;
      if (!variant || !variant.available || !button || !cart) return;
      root.dataset.addingBottle = 'true';
      root.classList.add('is-adding');
      root.classList.remove('is-added');

      const formData = new FormData();
      formData.append('id', variant.id);
      formData.append('quantity', '1');
      formData.append(
        'sections',
        cart.getSectionsToRender().map((section) => section.id)
      );
      formData.append('sections_url', window.location.pathname);

      button.disabled = true;
      button.textContent = 'Adding bottle...';
      if (message) message.textContent = 'Adding bottle to your cart...';

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      fetch(routes.cart_add_url, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) throw new Error(response.description || 'Unable to add bottle.');
          return fetch(`${routes.cart_url}.js`, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          })
            .then((cartResponse) => cartResponse.json())
            .then((cartState) => {
              const bottleInCart = cartState.items.some((item) => String(item.variant_id) === String(variant.id));
              if (!bottleInCart) throw new Error('Bottle was not added. Please try again.');
              return response;
            });
        })
        .then(() => {
          dismissCaffynBottleUpsell();
          root.dataset.addingBottle = 'false';
          delete cart.dataset.caffynShowUpsellStep;
          cart.classList.remove('is-empty');
          cart.hideCaffynUpsellStep();
          root.classList.remove('is-adding');
          root.classList.add('is-added');
          button.textContent = 'Bottle added';
          if (skipButton) skipButton.textContent = 'Continue to checkout';
          if (message) message.textContent = 'Bottle added. Continue to checkout.';
        })
        .catch((error) => {
          root.dataset.addingBottle = 'false';
          root.classList.remove('is-adding', 'is-added');
          if (message) message.textContent = error.message || 'Unable to add bottle.';
          syncVariant();
        });
    };

    colorButtons.forEach((swatch) => {
      swatch.addEventListener('click', () => {
        colorButtons.forEach((item) => item.classList.toggle('is-selected', item === swatch));
        syncVariant();
      });
    });

    sizeButtons.forEach((sizeButton) => {
      sizeButton.addEventListener('click', () => {
        sizeButtons.forEach((item) => item.classList.toggle('is-selected', item === sizeButton));
        syncVariant();
      });
    });

    root.caffynAddBottle = addBottle;
    if (button) button.addEventListener('click', addBottle);

    syncVariant();
  });
}

function handleCaffynBottleAdd(event) {
  const addButton = event.target.closest('[data-caffyn-bottle-add]');
  if (!addButton) return;

  const root = addButton.closest('[data-caffyn-cart-upsell]');
  if (!root) return;

  event.preventDefault();
  event.stopPropagation();
  if (!root.caffynAddBottle) initCaffynCartUpsells(root.parentElement || document);
  if (root.caffynAddBottle) root.caffynAddBottle();
}

function handleCaffynBottleSkip(event) {
  const skipButton = event.target.closest('[data-caffyn-bottle-skip]');
  if (!skipButton) return;

  event.preventDefault();
  event.stopPropagation();
  const root = skipButton.closest('[data-caffyn-cart-upsell]');
  if (root?.dataset.addingBottle === 'true') return;
  dismissCaffynBottleUpsell();
  goToCaffynCheckout();
}

document.addEventListener('DOMContentLoaded', () => initCaffynCartUpsells());
document.addEventListener('click', handleCaffynBottleAdd, true);
document.addEventListener('click', handleCaffynBottleSkip, true);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
